import { Changeset } from 'ember-changeset';
import { BufferedChangeset } from 'ember-changeset/types';
import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import { get } from 'lodash';
import CollectionView, { CollectionViewResourceName } from 'volta/models/collection-view';
import { POGroupResourceName } from 'volta/models/po-group';
import { IDashboardWidgetWithComponent } from 'volta/models/types/user';
import StoreService from 'volta/services/store';
import { normalize } from 'volta/utils/api/serialize-and-push';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  widget: IDashboardWidgetWithComponent;
  onUpdate: (widget: IDashboardWidgetWithComponent) => void;
}

export default class WidgetEditorPlanningOrders extends Component<IArgs> {
  @service store!: StoreService;
  @service intl!: IntlService;

  @tracked widgetArgs = this.args.widget.args ?? {};
  @tracked argChangeset!: BufferedChangeset;

  @tracked collectionViews: CollectionView[] = [];
  @tracked selectedView?: CollectionView;

  idKey: keyof CollectionView = 'id';
  propertyKey: keyof CollectionView = 'name';
  placeholder = this.intl.t('collectionViewsPage.default.name', {
    resourceNamePlural: this.intl.t(POGroupResourceName.plural)
  });

  get selectedId() {
    return this.selectedView?.id ?? this.args.widget.args?.collectionViewId;
  }

  // Actions
  // ~~~~~

  @action
  onChange(value: CollectionView) {
    const { idKey } = this;
    const idValue = isPresent(value) ? get(value, idKey) : value;

    this.selectedView = value;
    this.argChangeset.set('collectionViewId', idValue);
    this.argChangeset.set('collectionViewName', value?.name);

    this.updateWidget();
  }

  fetchCollectionViews = restartableTask(async () => {
    const data = await this.store.rawQuery(CollectionViewResourceName.plural, [
      'mine',
      POGroupResourceName.plural
    ]);
    this.collectionViews = normalize(CollectionViewResourceName.singular, data) as CollectionView[];

    if (this.args.widget.args?.collectionViewId) {
      this.selectedView = this.collectionViews.find((r) => r.id === this.selectedId);
    }
  });

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  @action
  updateWidget(): void {
    this.argChangeset.validate();

    if (this.argChangeset.isValid) {
      this.argChangeset.execute();
      this.args.onUpdate({ ...this.args.widget, args: this.widgetArgs });
    }
  }

  @action
  initChangeset() {
    this.argChangeset = Changeset(this.widgetArgs);
  }
}
